import React from 'react';

import EntryActions from 'actions/EntryActions';
import * as OnboardingActions from 'actions/OnboardingActions';
import BenchlingConfig from 'BenchlingConfig';
import * as benchlingUtil from 'BenchlingUtil';
import {NucleotideType, ResidueType} from 'biojs/BioConstants';
import BulkCreateInventoryFilesFlowClass from 'bulk-import/BulkCreateInventoryFilesFlow';
import {openBulkImportMixtureIngredientsFlow} from 'bulk-import/BulkImportMixtureIngredientsFlow';
import BulkUpdateBioentitiesFlowClass from 'bulk-import/BulkUpdateBioentitiesFlow';
import MoleculeImportModal from 'chemistry/bulk-upload/MoleculeImportModal';
import CreateMoleculeModal from 'chemistry/molecules/CreateMoleculeModal';
import * as FolderConstants from 'constants/FolderConstants';
import {StringIDFolder} from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import OnboardingConstants from 'constants/OnboardingConstants';
import EntryDAO from 'dao/EntryDAO';
import CreateDatasetModal from 'data-frames/modals/CreateDatasetModal';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import CreateFileModal from 'files/CreateFileModal';
import FileStates from 'FileStates';
import CreateEntryModal, {CreateEntryModalProps} from 'folder-items/CreateEntryModal';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import CreateFolderModalClass from 'folders/CreateFolderModal';
import normalizeFolder from 'folders/util/normalizeFolder';
import ImportSequenceModalClass from 'import-sequence/ImportSequenceModal';
import BulkReimportSequencesModal from 'import-sequence/reimport/BulkReimportSequencesModal';
import navigateToURL from 'lib/navigateToURL';
import {ComplexPolymerSchemaConfigGraphQL} from 'modbio/complex-polymers/ComplexPolymerUtil';
import AaSequenceActions from 'molbio/aa-sequences/AaSequenceActions';
import AaSequenceDAO from 'molbio/aa-sequences/AaSequenceDAO';
import {handleOpenBulkAssembleDNA} from 'molbio/bulk-assembly/util/BulkAssemblyUtil';
import {openCRISPRModal} from 'molbio/crispr/CRISPRModal';
import ImportSequenceForHRTemplateModal from 'molbio/crispr/hr-template/ImportSequenceForHRTemplateModal';
import {bulkReimportSequences} from 'molbio/dao/SequenceDAO';
import {DNA_SEQUENCE_IMPORT_FLOW} from 'molbio/MolBioConstants';
import CreateOligoModal from 'molbio/oligos/CreateOligoModal';
import CreateEntryFromTemplateModal, {
  CreateEntryFromTemplateModalProps,
} from 'notebook/entry-templates/CreateEntryFromTemplateModal/CreateEntryFromTemplateModal';
import LegacyCreateEntryFromTemplateModalClass from 'notebook/entry-templates/LegacyCreateEntryFromTemplateModal';
import ProtocolDAO from 'notebook/protocols/dao/ProtocolDAO';
import * as RegistryUtil from 'registry/RegistryUtil';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import {SearchParams} from 'search/SearchConstants';
import {StudyAssociation} from 'studies/StudyableDAO';
import {ZINDEX_MODAL} from 'styles/StyleConstants';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import legacyRunModal from 'ui-library/legacyRunModal';
import runModal from 'ui-library/runModal';

// TODO(BNCH-38468): Remove feature flag after rollout and discontinue lagacy modal (this method likely not needed)
export function createEntry(folderId: string): void {
  FolderDefaults.set(FolderDefaultsKey.NOTEBOOK, folderId);
  const promiseForFolder = EntryDAO.create({name: 'Untitled', folder: folderId}).done((folderResult) => {
    OnboardingActions.completeOnboardingTask(
      OnboardingConstants.MODULES.ENTRY,
      OnboardingConstants.CREATE_ENTRY
    );
    return folderResult;
  });
  openFileAfter(promiseForFolder);
}

export function createAaSequence(folderId: string): void {
  FolderDefaults.set(FolderDefaultsKey.INVENTORY, folderId);
  AaSequenceActions.promptCreateAndOpen(folderId);
}

export function createProtocol(folderId: string): void {
  FolderDefaults.set(FolderDefaultsKey.NOTEBOOK, folderId);
  openFileAfter(ProtocolDAO.create({folder: folderId, sharing: 'private'}));
}

export function createSequence(folderId: string): void {
  FolderDefaults.set(FolderDefaultsKey.INVENTORY, folderId);
  legacyRunModal(ImportSequenceModalClass, {
    initialTab: DNA_SEQUENCE_IMPORT_FLOW.RAW,
  });
}

function handleCloseFolderModel(): void {
  LegacyModalContainer.close(CreateFolderModalClass);
}

export function handleCreateEntryFromTemplate({
  entryName,
  folderId,
  templateId,
  tagSchemaId,
  schemaTags,
  initialDay,
  studyAssociations,
}: {
  entryName: string;
  folderId: string;
  templateId: string;
  tagSchemaId?: string;
  schemaTags?: TagUpdateObject;
  initialDay: string;
  studyAssociations?: Array<StudyAssociation>;
}): void {
  EntryActions.createFromTemplate({
    entryName,
    folderId,
    templateId,
    tagSchemaId,
    schemaTags,
    initialDay,
    studyAssociations,
    // eslint-disable-next-line @typescript-eslint/unbound-method
    onSuccess: LegacyCreateEntryFromTemplateModalClass.close,
  });
}

export function openBatchSequenceImportModal(folderId?: string): void {
  legacyRunModal(ImportSequenceModalClass, {
    targetFolderId: folderId,
    initialTab: DNA_SEQUENCE_IMPORT_FLOW.CHROMOSOME,
  });
}

export function openBulkAssembleDNA(): void {
  handleOpenBulkAssembleDNA();
}

export function openBulkCreateInventoryFilesFlow(
  folder: StringIDFolder,
  complexPolymerSchemaConfigurations?: Array<ComplexPolymerSchemaConfigGraphQL>,
  shouldFilterToComplexPolymerSchemas?: boolean
): void {
  BulkCreateInventoryFilesFlowClass.open({
    initialFolder: _getInitialFolder(folder),
    complexPolymerSchemaConfigurations,
    shouldFilterToComplexPolymerSchemas,
  });
}

export function openBulkReimportAaSequences(folderId: string): void {
  legacyRunModal(BulkReimportSequencesModal, {
    initialFolderId: folderId,
    residueType: ResidueType.AA,
    onReimport: async (
      fileJSONs: Array<{name: string; s3Location: string}>,
      aaSequenceFolderId: string,
      _?: NucleotideType
    ) => {
      const {sequenceIds} = await AaSequenceDAO.bulkReimportAaSequences(fileJSONs, aaSequenceFolderId);
      // This just refreshes any open proteins
      sequenceIds.forEach((id: string) => {
        AaSequenceActions.open(id, {});
      });
    },
  });
}

export function openBulkReimportSequences(folderId: string): void {
  legacyRunModal(BulkReimportSequencesModal, {
    initialFolderId: folderId,
    residueType: ResidueType.NUCLEOTIDE,
    onReimport: async (
      fileJSONs: Array<{name: string; s3Location: string}>,
      sequenceFolderId: string,
      nucleotideType?: NucleotideType
    ) => {
      const {sequenceIds} = await bulkReimportSequences(fileJSONs, sequenceFolderId, nucleotideType);
      await FileStates.get().updateSequenceVersions(sequenceIds);
    },
  });
}

export function openBulkTranslateDNAtoAA(): void {
  benchlingUtil.navigate(null, '/bulk-workflows/translation');
}

export function openBulkUpdateBioentitiesModal(
  folder: FolderConstants.StringIDFolder | null,
  complexPolymerSchemaConfigurations: Array<ComplexPolymerSchemaConfigGraphQL>
): void {
  const initialFolder = folder == null ? null : _getInitialFolder(folder);
  BulkUpdateBioentitiesFlowClass.open({initialFolder, complexPolymerSchemaConfigurations});
}

export function openBulkUpdateMixtureIngredientsModal(folder: FolderConstants.StringIDFolder): void {
  openBulkImportMixtureIngredientsFlow({initialFolder: _getInitialFolder(folder)});
}

export function openCreateFileModal(folderId: string): void {
  runModal(CreateFileModal, {initialFolderId: folderId}, {title: 'Create file', zIndex: ZINDEX_MODAL}).then(
    (createdFileIds) => {
      if (createdFileIds?.length === 1) {
        FileStates.get().openFolderItem(createdFileIds[0]);
      }
    }
  );
}

export function openCreateDatasetModal(folderId: string): void {
  runModal(
    CreateDatasetModal,
    {initialFolderId: folderId},
    {title: 'Create dataset', zIndex: ZINDEX_MODAL}
  ).then((datasetId) => {
    if (datasetId != null) {
      FileStates.get().openFolderItem(datasetId);
    }
  });
}

export function openCreateOligoModal(folderId: string): void {
  legacyRunModal(CreateOligoModal, {initialFolderId: folderId});
}

export function openCreateMoleculeModal(folderId: string | null): void {
  legacyRunModal(CreateMoleculeModal, {initialFolderId: folderId});
}

export function openImportMoleculesFromSpreadsheet(folderId: string | null): void {
  if (BenchlingConfig.CHEMISTRY_ENABLE_STOICHIOMETRY_TABLE) {
    const initialFolderId = folderId ?? FolderDefaults.get(FolderDefaultsKey.INVENTORY);
    runModal(
      MoleculeImportModal,
      {initialFolderId},
      {
        title: 'Import Molecules',
        size: 'lg',
        // Match legacy modal behavior and preserve in-progress wizard state.
        closeOnEscape: false,
        // Keep theme/token-driven overlay color and only customize opacity.
        overlayProps: {opacity: 0.3},
        // Required so nested legacy FolderSelectorModal can receive wheel/trackpad scroll events.
        lockScroll: false,
        // zIndex below FolderSelector modal (1040) and AbsoluteDropdownContainer (1051)
        // so nested modals and dropdowns render above this modal
        zIndex: 1030,
        styles: {
          body: {
            minHeight: 'min(640px, 80vh)',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column' as const,
          },
        },
      }
    );
    return;
  }
  BulkCreateInventoryFilesFlowClass.open({
    initialFolder: _getInitialFolderWithDefault(folderId, FolderDefaultsKey.INVENTORY),
    title: 'Import molecules',
    onFolderChange: (newFolderId: string) => {
      FolderDefaults.set(FolderDefaultsKey.INVENTORY, newFolderId);
    },
    isSchemaRequired: false,
    schemalessCreationEntityType: ModelTypeConstants.MOLECULE,
  });
}

export function openImportComplexPolymersFromSpreadsheet(
  folderId: string | null,
  complexPolymerSchemaConfigurations: Array<ComplexPolymerSchemaConfigGraphQL>
): void {
  BulkCreateInventoryFilesFlowClass.open({
    initialFolder: _getInitialFolderWithDefault(folderId, FolderDefaultsKey.INVENTORY),
    title: 'Import oligo duplexes or oligo conjugates',
    onFolderChange: (newFolderId: string) => {
      FolderDefaults.set(FolderDefaultsKey.INVENTORY, newFolderId);
    },
    isSchemaRequired: true,
    complexPolymerSchemaConfigurations,
    shouldFilterToComplexPolymerSchemas: true,
  });
}

export function openCRISPRGuideModal(folderId: string): void {
  const onClose = (): void => {
    FolderDAOCache.invalidateFolderContents(folderId);
  };
  openCRISPRModal({onClose});
}

export function openFileAfter(promise: JQueryPromise<{}>): void {
  promise.done((result: StringIDFolder) => {
    FileBrowserActions.openFile(result.id);
  });
}

export function openImportOligoModal(folderId: string | null): void {
  BulkCreateInventoryFilesFlowClass.open({
    initialFolder: _getInitialFolderWithDefault(folderId, FolderDefaultsKey.OLIGO),
    schemalessCreationEntityType: ModelTypeConstants.DNA_OLIGO,
    title: 'Import DNA / RNA oligos from spreadsheet',
    onFolderChange: (newFolderId: string) => {
      FolderDefaults.set(FolderDefaultsKey.OLIGO, newFolderId);
    },
  });
}

export function openImportSequences(folderId: string): void {
  legacyRunModal(ImportSequenceModalClass, {targetFolderId: folderId});
}

export function openImportSequencesFromSpreadsheet(folderId: string | null): void {
  BulkCreateInventoryFilesFlowClass.open({
    initialFolder: _getInitialFolderWithDefault(folderId, FolderDefaultsKey.INVENTORY),
    schemalessCreationEntityType: ModelTypeConstants.SEQUENCE,
    title: 'Import DNA / RNA sequences from spreadsheet',
    onFolderChange: (newFolderId: string) => {
      FolderDefaults.set(FolderDefaultsKey.INVENTORY, newFolderId);
    },
  });
}

export function openImportSequenceForHRTemplateModal(folderId: string): void {
  const onRequestClose = (): void => {
    LegacyModalContainer.close(ImportSequenceForHRTemplateModal);
    FolderDAOCache.invalidateFolderContents(folderId);
  };
  LegacyModalContainer.open(
    ImportSequenceForHRTemplateModal,
    <ImportSequenceForHRTemplateModal onRequestClose={onRequestClose} />
  );
}

export function openImportAaSequencesFromSpreadsheet(folderId: string | null): void {
  BulkCreateInventoryFilesFlowClass.open({
    initialFolder: _getInitialFolderWithDefault(folderId, FolderDefaultsKey.INVENTORY),
    schemalessCreationEntityType: ModelTypeConstants.PROTEIN,
    title: 'Import AA sequences from spreadsheet',
    onFolderChange: (newFolderId: string) => {
      FolderDefaults.set(FolderDefaultsKey.INVENTORY, newFolderId);
    },
  });
}

export function openNewFolder(
  folderId: string,
  folderType: string,
  openResult?: (folder: StringIDFolder, searchParams?: SearchParams) => void
): void {
  LegacyModalContainer.open(
    CreateFolderModalClass,
    <CreateFolderModalClass
      onRequestClose={handleCloseFolderModel}
      successCallback={(newFolder: StringIDFolder) => {
        if (openResult != null) {
          newFolder = normalizeFolder(newFolder);
          openResult(newFolder);
          return;
        }
        navigateToURL(newFolder.url);
      }}
      initialParentId={folderId}
      folderType={folderType}
    />
  );
}

export type CreateEntryModalControlledProps = Pick<
  CreateEntryModalProps,
  | 'initialFolderId'
  | 'initialStudySearchItems'
  | 'displayChangeStudyableAssignmentPermissionWarning'
  | 'autoLinkingStudy'
  | 'onEntryCreated'
  | 'initialNoteParts'
>;

export function showCreateEntryModal(props: CreateEntryModalControlledProps): void {
  LegacyModalContainer.open(
    CreateEntryModal,
    <CreateEntryModal
      {...props}
      onRequestClose={() => {
        LegacyModalContainer.close(CreateEntryModal);
      }}
      fulfillments={[]}
    />
  );
}

export type CreateEntryFromTemplateModalControlledProps = Pick<
  CreateEntryFromTemplateModalProps,
  | 'initialFolderId'
  | 'filterTemplateIds'
  | 'initialStudySearchItems'
  | 'displayChangeStudyableAssignmentPermissionWarning'
  | 'autoLinkingStudy'
>;

export function showCreateEntryFromTemplateModal(props: CreateEntryFromTemplateModalControlledProps): void {
  // TODO(BNCH-38468): Remove feature flag after rollout and discontinue lagacy modal
  if (!BenchlingConfig.ELN_ENABLE_SCHEMA_ON_ENTRY_CREATE_MODAL) {
    LegacyCreateEntryFromTemplateModalClass.open({
      initialFolderId: props.initialFolderId,
      filterTemplateIds: props.filterTemplateIds,
      onRequestSubmit: handleCreateEntryFromTemplate,
    });
  } else {
    legacyRunModal(CreateEntryFromTemplateModal, {
      ...props,
      onRequestClose: () => {},
      onRequestSubmit: handleCreateEntryFromTemplate,
      isTemplateSelectorDisabled: false,
    });
  }
}

function _getInitialFolder(folder: FolderConstants.StringIDFolder): {
  folderId: string;
  registryId: string | null;
} {
  const isRegistry = folder != null ? RegistryUtil.isRegistry(folder.source) : false;
  return {
    folderId: folder.id,
    registryId: isRegistry ? folder.source!.id : null,
  };
}

function _getInitialFolderWithDefault(
  selectedFolderId: string | null,
  folderDefaultKey: FolderDefaultsKey
): {folderId: string; registryId: null} | null {
  const folderDefault = FolderDefaults.get(folderDefaultKey);
  if (selectedFolderId) {
    return {folderId: selectedFolderId, registryId: null};
  } else if (folderDefault) {
    return {folderId: folderDefault, registryId: null};
  } else {
    return null;
  }
}
